import React, { useState } from 'react';
import { Form, Input, Message, Modal, Button } from 'semantic-ui-react';
import styled from 'styled-components';
import { PageSection } from 'common/styles/page';

interface OrganizationRemindersFormProps {
  formValue: { default_reminder_schedule?: string };
  formErrors: { default_reminder_schedule?: string };
  onChange: (field: 'default_reminder_schedule', value: string) => void;
}

// Validate cron expression format
const validateCronExpression = (expr: string): { valid: boolean; error?: string } => {
  if (!expr || expr.trim() === '') {
    return { valid: false, error: 'Reminder schedule is required' };
  }

  const parts = expr.trim().split(/\s+/);

  if (parts.length !== 5) {
    return {
      valid: false,
      error: 'Cron expression must have exactly 5 parts: minute hour day_of_month month_of_year day_of_week',
    };
  }

  // Basic validation for each part
  const [minute, hour, dayOfMonth, monthOfYear, dayOfWeek] = parts;

  // Validate minute (0-59 or *)
  if (!isValidCronField(minute, 0, 59)) {
    return { valid: false, error: 'Invalid minute field (must be 0-59, *, or a valid expression)' };
  }

  // Validate hour (0-23 or *)
  if (!isValidCronField(hour, 0, 23)) {
    return { valid: false, error: 'Invalid hour field (must be 0-23, *, or a valid expression)' };
  }

  // Validate day of month (1-31 or *)
  if (!isValidCronField(dayOfMonth, 1, 31)) {
    return { valid: false, error: 'Invalid day of month field (must be 1-31, *, or a valid expression)' };
  }

  // Validate month (1-12 or *)
  if (!isValidCronField(monthOfYear, 1, 12)) {
    return { valid: false, error: 'Invalid month field (must be 1-12, *, or a valid expression)' };
  }

  // Validate day of week (0-7 or *)
  if (!isValidCronField(dayOfWeek, 0, 7)) {
    return { valid: false, error: 'Invalid day of week field (must be 0-7, *, or a valid expression)' };
  }

  return { valid: true };
};

const isValidCronField = (field: string, min: number, max: number): boolean => {
  // Allow wildcard
  if (field === '*') return true;

  // Allow ranges (e.g., 1-5)
  if (field.includes('-')) {
    const [start, end] = field.split('-').map(Number);
    return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
  }

  // Allow lists (e.g., 1,3,5)
  if (field.includes(',')) {
    const values = field.split(',').map(Number);
    return values.every(v => !isNaN(v) && v >= min && v <= max);
  }

  // Allow step values (e.g., */5)
  if (field.includes('/')) {
    const [range, step] = field.split('/');
    const stepNum = Number(step);
    if (isNaN(stepNum) || stepNum < 1) return false;
    if (range === '*') return true;
    const rangeNum = Number(range);
    return !isNaN(rangeNum) && rangeNum >= min && rangeNum <= max;
  }

  // Allow single number
  const num = Number(field);
  return !isNaN(num) && num >= min && num <= max;
};

const OrganizationRemindersForm: React.FC<OrganizationRemindersFormProps> = ({
  formValue,
  formErrors,
  onChange,
}) => {
  const [cronHelpOpen, setCronHelpOpen] = useState(false);

  const validation = formValue.default_reminder_schedule
    ? validateCronExpression(formValue.default_reminder_schedule)
    : { valid: true };

  return (
    <PageSection>
      <InfoSection>
        <SectionTitle>Automated Reminders</SectionTitle>
        <InfoText>
          Configure the default participant email reminder schedule for new assessment periods. This schedule will be
          automatically applied when creating new assessment periods, but can be customized per period.
        </InfoText>
      </InfoSection>

      <Form>
        <Form.Group widths="equal">
          <Form.Field
            required
            style={{ display: 'flex', alignItems: 'flex-start' }}
            error={!!formErrors.default_reminder_schedule || !validation.valid}
          >
            <label style={{ minWidth: 180, marginRight: '1rem', paddingTop: '0.5rem' }}>
              Default Reminder Schedule
            </label>
            <div style={{ flex: 1 }}>
              <Input
                placeholder="0 9 * * 1,3,5"
                value={formValue.default_reminder_schedule ?? '0 9 * * 1,3,5'}
                onChange={(_, { value }) => onChange('default_reminder_schedule', value)}
                style={{ width: 400 }}
                error={!!formErrors.default_reminder_schedule || !validation.valid}
              />
              <HelpRow>
                <HelpText>
                  Cron expression for default reminder schedule (e.g., "0 9 * * 1,3,5" = Mon/Wed/Fri at 9am UTC)
                </HelpText>
                <LearnButton type="button" onClick={() => setCronHelpOpen(true)}>
                  More about cron...
                </LearnButton>
              </HelpRow>

              {!validation.valid && <ErrorText>{validation.error}</ErrorText>}
              {validation.valid && (
                <SuccessText>✓ Valid cron expression</SuccessText>
              )}
            </div>
          </Form.Field>
        </Form.Group>

        <QuickSchedulesSection>
          <strong>Quick options:</strong>
          <ScheduleButton onClick={() => onChange('default_reminder_schedule', '0 9 * * 1,3,5')}>
            Mon/Wed/Fri 9am
          </ScheduleButton>
          <ScheduleButton onClick={() => onChange('default_reminder_schedule', '0 10 * * 1-5')}>
            Weekdays 10am
          </ScheduleButton>
          <ScheduleButton onClick={() => onChange('default_reminder_schedule', '0 14 * * *')}>
            Daily 2pm
          </ScheduleButton>
          <ScheduleButton onClick={() => onChange('default_reminder_schedule', '0 9,17 * * *')}>
            Twice daily (9am &amp; 5pm)
          </ScheduleButton>
        </QuickSchedulesSection>
      </Form>

      {/* Cron Help Modal */}
      <Modal
        open={cronHelpOpen}
        onClose={() => setCronHelpOpen(false)}
        size="small"
        closeIcon
      >
        <Modal.Header>Learn about cron schedules</Modal.Header>
        <Modal.Content scrolling>
          <ExamplesSection>
            <SectionTitle>Common Cron Expression Examples</SectionTitle>
            <ExampleTable>
              <thead>
                <tr>
                  <th>Expression</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><code>0 9 * * 1,3,5</code></td>
                  <td>Mon, Wed, Fri at 9:00 AM UTC</td>
                </tr>
                <tr>
                  <td><code>0 10 * * 1-5</code></td>
                  <td>Weekdays (Mon–Fri) at 10:00 AM UTC</td>
                </tr>
                <tr>
                  <td><code>0 14 * * *</code></td>
                  <td>Every day at 2:00 PM UTC</td>
                </tr>
                <tr>
                  <td><code>0 9,17 * * *</code></td>
                  <td>Every day at 9:00 AM and 5:00 PM UTC</td>
                </tr>
                <tr>
                  <td><code>0 9 * * 1</code></td>
                  <td>Every Monday at 9:00 AM UTC</td>
                </tr>
                <tr>
                  <td><code>0 */2 * * *</code></td>
                  <td>Every 2 hours</td>
                </tr>
              </tbody>
            </ExampleTable>
          </ExamplesSection>

          <FormatSection>
            <SectionTitle>Cron Expression Format</SectionTitle>
            <FormatText>
              <strong>Format:</strong>{' '}
              <code>minute hour day_of_month month_of_year day_of_week</code>
            </FormatText>
            <FormatDetails>
              <li><strong>minute:</strong> 0–59</li>
              <li><strong>hour:</strong> 0–23 (UTC timezone)</li>
              <li><strong>day of month:</strong> 1–31</li>
              <li><strong>month:</strong> 1–12</li>
              <li><strong>day of week:</strong> 0–7 (0 and 7 = Sunday)</li>
            </FormatDetails>
            <FormatText style={{ marginTop: '12px' }}>
              <strong>Special characters:</strong>
            </FormatText>
            <FormatDetails>
              <li><code>*</code> = any value</li>
              <li><code>,</code> = list (e.g., <code>1,3,5</code>)</li>
              <li><code>-</code> = range (e.g., <code>1-5</code>)</li>
              <li><code>/</code> = step (e.g., <code>*/2</code> = every 2)</li>
            </FormatDetails>
          </FormatSection>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setCronHelpOpen(false)} primary>
            Close
          </Button>
        </Modal.Actions>
      </Modal>
    </PageSection>
  );
};

export default OrganizationRemindersForm;

// Export validation function for use in other components
export { validateCronExpression };

/* styled components */

const InfoSection = styled.div`
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
  margin: 0;
`;

const HelpRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 12px;
  flex-wrap: wrap;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.5;
`;

const LearnButton = styled.button`
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 4px;
  border: 1px solid #ccc;
  background: #ffffff;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: #f3f3f3;
    border-color: #bdbdbd;
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #db2828;
  margin-top: 8px;
  font-weight: 500;
`;

const SuccessText = styled.div`
  font-size: 12px;
  color: #21ba45;
  margin-top: 8px;
  font-weight: 500;
`;

const QuickSchedulesSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  margin: 24px 0;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const ScheduleButton = styled.button`
  padding: 6px 12px;
  font-size: 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #e8e8e8;
    border-color: #bbb;
  }
`;

const ExamplesSection = styled.div`
  margin-top: 8px;
`;

const ExampleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;
  font-size: 13px;

  th,
  td {
    padding: 10px;
    text-align: left;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    background: #f8f9fa;
    font-weight: 600;
    color: #333;
  }

  code {
    background: #f4f4f4;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }
`;

const FormatSection = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
`;

const FormatText = styled.div`
  font-size: 13px;
  color: #555;
  margin-bottom: 8px;

  code {
    background: #e8e8e8;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }
`;

const FormatDetails = styled.ul`
  list-style: none;
  padding-left: 0;
  margin: 8px 0;
  font-size: 13px;
  color: #666;

  li {
    padding: 4px 0;
  }

  code {
    background: #e8e8e8;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }
`;
